import { Avatar, Flex, Text } from "@radix-ui/themes";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { FaChevronDown } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";

import ArtistProfileIcon from "@/assets/icons/Artist user account.svg?react";
import { cn } from "@/lib/utils";
import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import ArtistSwitcherMenu, { ArtistAvatar } from "./ArtistSwitcherMenu";

type ArtistSelectorProps = {
  // compact: small selector for modal headers (upload modal).
  variant: "nav" | "modal" | "compact";
  className?: string;
};

export default function ArtistSelector({
  variant,
  className,
}: ArtistSelectorProps) {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { selectedCreator } = useAudiolab();
  const { canSwitchArtists } = useLoggedInUser();
  const hasSideRoom = useMediaQuery("(min-width: 1024px)");
  const opensSideways = hasSideRoom && variant !== "compact";

  const isActive =
    variant === "nav" &&
    !!matchRoute({ to: "/audiolab/creators/$CREATOR_ID", fuzzy: true });

  const displayName = selectedCreator?.stageName || "Artist Profile";
  const avatarUrl = selectedCreator?.profilePictureUrl;

  const handleProfileClick = () => {
    if (selectedCreator) {
      navigate({
        to: "/audiolab/creators/$CREATOR_ID",
        params: { CREATOR_ID: selectedCreator.creatorId },
      });
    }
  };

  // Single-artist accounts keep the original nav-row experience (profile link
  // styled like a nav item). The dropdown card is for accounts that can switch.
  if (!canSwitchArtists) {
    return (
      <Flex
        data-testid="artist-selector"
        align="center"
        className={cn("w-full", className)}
      >
        <Flex
          data-testid="artist-selector-profile"
          align="center"
          className={cn(
            "flex-1 min-w-0 h-[35px] gap-[8px] cursor-pointer pl-[5px] pr-[4px] rounded-[5px] hover:bg-background-quarternary transition-all",
            isActive && "bg-background-quarternary !text-brand-primary",
          )}
          onClick={handleProfileClick}
        >
          {avatarUrl ? (
            <div className="w-[22px] h-[22px] rounded-full overflow-hidden shrink-0">
              <Avatar
                src={avatarUrl}
                fallback={
                  <ArtistProfileIcon
                    width={22}
                    height={22}
                    className="fill-text-secondary"
                  />
                }
                size="1"
                radius="full"
                className="w-full h-full"
              />
            </div>
          ) : (
            <ArtistProfileIcon
              width={22}
              height={22}
              className="fill-text-secondary shrink-0"
            />
          )}
          <Text
            data-testid="artist-selector-name"
            className="font-semibold text-[14px] truncate"
          >
            {displayName}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <ArtistSwitcherMenu
      // The side flyout fits next to the desktop sidebar; on smaller
      // viewports (e.g. inside the upload modal on a phone) there is no
      // side room, so the list drops downward instead of clamping onscreen
      // over the trigger.
      side={opensSideways ? "right" : "bottom"}
      sideOffset={8}
      align={opensSideways ? "start" : "end"}
      // Non-modal: this trigger can sit inside a modal Dialog (upload
      // wizard), and nested modal layers double-lock pointer events on
      // touch — the menu would close right after opening.
      modal={false}
      trigger={
        variant === "compact" ? (
          <button
            data-testid="artist-selector"
            type="button"
            className={cn(
              "group flex h-10 min-w-0 items-center gap-2 rounded-[10px] border border-border-secondary bg-white pl-1.5 pr-3 cursor-pointer outline-none transition-all",
              "hover:bg-background-tertiary data-[state=open]:bg-background-tertiary",
              className,
            )}
          >
            <ArtistAvatar
              url={avatarUrl}
              size={28}
              iconSize={18}
              className="ring-1 ring-black/10"
            />
            <Text
              data-testid="artist-selector-name"
              className="truncate text-[13px] font-semibold text-blue-900"
            >
              {displayName}
            </Text>
            <FiChevronDown
              size={16}
              className="ml-2 shrink-0 text-text-secondary transition-transform group-data-[state=open]:rotate-180"
            />
          </button>
        ) : (
          <button
            data-testid="artist-selector"
            type="button"
            className={cn(
              "w-full rounded-[10px] bg-white border border-border-secondary overflow-hidden transition-all",
              "group flex items-center gap-[10px] py-[10px] pl-[10px] pr-[12px] cursor-pointer outline-none",
              "hover:bg-background-tertiary data-[state=open]:bg-background-tertiary",
              isActive && "ring-2 ring-brand-primary border-transparent",
              className,
            )}
          >
            <ArtistAvatar
              url={avatarUrl}
              size={44}
              iconSize={24}
              className={cn(
                "ring-1 ring-black/10",
                isActive && "ring-2 ring-brand-primary",
              )}
            />
            <Flex direction="column" className="min-w-0 flex-1 text-left">
              {selectedCreator && (
                <Text className="text-[9px] font-bold uppercase tracking-[0.08em] leading-[12px] text-text-tertiary">
                  Selected artist
                </Text>
              )}
              <Text
                data-testid="artist-selector-name"
                className="font-semibold text-[14px] leading-[18px] text-blue-900 line-clamp-2 break-words"
              >
                {displayName}
              </Text>
            </Flex>
            <FaChevronDown
              size={10}
              className="shrink-0 text-text-secondary transition-transform group-data-[state=open]:rotate-180"
            />
          </button>
        )
      }
    />
  );
}
